import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { NotificationBell } from "./NotificationBell";
import { DiscordButton } from "./DiscordButton";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";
import { hunterTier } from "../lib/constants";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Plus, ShieldCheck, LogOut, User, Crown, FileText, BarChart3, CreditCard, Crosshair, Dumbbell } from "lucide-react";

export const Navbar = () => {
  const { user, isMod, logout, openAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const manageSubscription = async () => {
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't open the subscription portal. Try again.");
    }
  };

  const navLink = (to, label, testid) => {
    const active = location.pathname === to;
    return (
      <Link
        key={to}
        to={to}
        data-testid={testid}
        className={`text-sm font-semibold transition-colors ${
          active ? "text-[#00F0FF]" : "text-[#94A3B8] hover:text-white"
        }`}
      >
        {label}
      </Link>
    );
  };

  const handleReport = () => {
    if (user) navigate("/submit");
    else openAuth();
  };

  return (
    <header className="sticky top-0 z-50 border-b border-[#1E293F] bg-[#090C15]/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src="/logo.webp" alt="2K Case Files" className="h-11 w-auto object-contain" />
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          {navLink("/", "Wall of Shame", "nav-wall")}
          {navLink("/search", "Search", "nav-search")}
          {/* Build Lab hidden from live site — re-enable to launch */}
          {/* {navLink("/builder", "Build Lab", "nav-builder")} */}
          {navLink("/stats", "Stats", "nav-stats")}
          {navLink("/about", "About", "nav-about")}
          {navLink("/faq", "FAQ", "nav-faq")}
          {user && navLink("/submit", "Report", "nav-report")}
          {isMod && navLink("/admin", "Mod Queue", "nav-admin")}
        </nav>

        <div className="flex items-center gap-3">
          <DiscordButton />
          <Button
            data-testid="report-cta-btn"
            onClick={handleReport}
            className="bg-[#FF2A55] font-bold text-white hover:bg-[#FF2A55]/90"
            size="sm"
          >
            <Plus size={16} className="mr-1" /> Report Cheater
          </Button>

          {user && <NotificationBell />}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-[#1E293F] bg-[#141B2D] text-[#00F0FF]">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <User size={16} />
                  )}
                  {user.is_supporter && (
                    <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#FFB800] text-[#090C15]">
                      <Crown size={10} />
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-[#1E293F] bg-[#0F1424] text-white">
                <div className="px-2 py-1.5">
                  <div className="flex items-center gap-1.5 text-sm font-semibold">
                    {user.name}
                    {user.is_supporter && <Crown size={13} className="text-[#FFB800]" />}
                  </div>
                  <div className="text-xs text-[#64748B]">{user.email}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    {isMod && <span className="inline-block rounded bg-[#00F0FF]/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#00F0FF]">Modder</span>}
                    {user.confirmed_busts > 0 && (() => { const t = hunterTier(user.confirmed_busts); return (
                      <span data-testid="menu-hunter-badge" className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase" style={{ color: t.color, backgroundColor: `${t.color}26` }}>
                        <Crosshair size={11} /> {t.short} ·{user.confirmed_busts}
                      </span>
                    ); })()}
                  </div>
                </div>
                <DropdownMenuSeparator className="bg-[#1E293F]" />
                <DropdownMenuItem data-testid="menu-my-reports" onClick={() => navigate("/my")} className="cursor-pointer focus:bg-[#1A233A] focus:text-white">
                  <FileText size={15} className="mr-2" /> My Reports & Profile
                </DropdownMenuItem>
                {/* My Builds hidden from live site — re-enable to launch Build Lab */}
                {/* <DropdownMenuItem data-testid="menu-my-builds" onClick={() => navigate("/builds")} className="cursor-pointer focus:bg-[#1A233A] focus:text-white">
                  <Dumbbell size={15} className="mr-2" /> My Builds
                </DropdownMenuItem> */}
                <DropdownMenuItem data-testid="menu-stats" onClick={() => navigate("/stats")} className="cursor-pointer focus:bg-[#1A233A] focus:text-white">
                  <BarChart3 size={15} className="mr-2" /> Stats Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-supporter" onClick={() => navigate("/supporter")} className="cursor-pointer text-[#FFB800] focus:bg-[#1A233A] focus:text-[#FFB800]">
                  <Crown size={15} className="mr-2" /> {user.is_supporter ? "Supporter Perks" : "Become a Supporter"}
                </DropdownMenuItem>
                {user.is_supporter && (
                  <DropdownMenuItem data-testid="menu-manage-subscription" onClick={manageSubscription} className="cursor-pointer focus:bg-[#1A233A] focus:text-white">
                    <CreditCard size={15} className="mr-2" /> Manage Subscription
                  </DropdownMenuItem>
                )}
                {!isMod && (
                  <DropdownMenuItem data-testid="menu-apply" onClick={() => navigate("/apply")} className="cursor-pointer focus:bg-[#1A233A] focus:text-white">
                    <ShieldCheck size={15} className="mr-2" /> Apply to be a Modder
                  </DropdownMenuItem>
                )}
                {isMod && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-[#1A233A] focus:text-white">
                    <ShieldCheck size={15} className="mr-2" /> Mod Queue
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer text-[#FF2A55] focus:bg-[#1A233A] focus:text-[#FF2A55]">
                  <LogOut size={15} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              data-testid="signin-btn"
              onClick={openAuth}
              variant="outline"
              size="sm"
              className="border-[#1E293F] bg-transparent text-white hover:bg-[#141B2D] hover:text-white"
            >
              Sign In
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};
